import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, X } from "lucide-react";

import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BulkInquiryForm } from "@/components/BulkInquiryForm";
import { TeamInterestForm } from "@/components/TeamInterestForm";
import { DiscountOffer } from "@/components/DiscountOffer";
import { useReveal } from "@/hooks/use-reveal";
import { DemoDoor } from "@/components/DemoDoor";

const BULK = [
  { range: "5 – 9 licenses", off: "10% off", note: "Small leadership teams and partnerships." },
  { range: "10 – 24 licenses", off: "20% off", note: "Operating groups and portfolio companies." },
  { range: "25+ licenses", off: "30% off", note: "Custom terms, invoicing and onboarding." },
];

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — The Founders App private operating system" },
      {
        name: "description",
        content:
          "The Founders App pricing: free beta seats in exchange for weekly feedback, then Commitment for one business, Operator for everything you run, Vault for the serious operator.",
      },
      { property: "og:title", content: "Pricing — The Founders App private operating system" },
      {
        property: "og:description",
        content: "Free during beta in exchange for weekly feedback. No ads, no resale, no attention farming.",

      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/pricing" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/pricing" }],
  }),
  component: Pricing,
});

const TIERS = [
  {
    code: "01",
    name: "Commitment",
    monthly: 9,
    annual: 79,
    line: "One business. Full discipline.",
    features: [
      "One business",
      "Daily move + evening close",
      "Monthly bold move",
      "Journal and contact log",
      "Four-digit lock with lockout protection",
      "7-day money-back guarantee",
    ],
    cta: "Start free in beta",
  },
  {
    code: "02",
    name: "Operator",
    monthly: 19,
    annual: 179,
    line: "Everything you run, side by side.",
    features: [
      "Everything in Commitment",
      "Unlimited businesses",
      "Timeline filters by type and category",
      "Contact interaction history",
      "Encrypted sync across your devices",
      "7-day money-back guarantee",
    ],
    cta: "Start free in beta",
    featured: true,
  },
  {
    code: "03",
    name: "Vault",
    monthly: 39,
    annual: 349,
    line: "For the people who never leave a trail.",
    features: [
      "Everything in Operator",
      "One-tap export of every record (JSON)",
      "Priority support line",
      "Early access to new instruments",
      "Concierge onboarding",
      "7-day money-back guarantee",
    ],
    cta: "Start free in beta",
  },
];

const money = (n: number) => `$${n.toLocaleString("en-US")}`;
const perDay = (annual: number) => {
  const cents = Math.round((annual / 365) * 100) / 100;
  return cents < 1 ? `${Math.round(cents * 100)} cents a day` : `$${cents.toFixed(2)} a day`;
};


function Pricing() {
  useReveal();
  const [cadence, setCadence] = useState<"annual" | "monthly">("annual");


  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-6xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Pricing</p>
        <h1 className="reveal etched mt-5 max-w-3xl pb-1 text-[2.5rem] leading-[1.08] font-semibold sm:text-6xl [--d:80ms]">
          Free during beta. Paid when it's worth paying for.
        </h1>
        <p className="reveal mt-6 max-w-lg text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          We don't farm your attention with ads or upsell you feature by feature. While we're in
          beta, a seat is free — in exchange we ask for one useful note a week: something that broke,
          something missing, or something you'd change. No card, no invitation, no waiting list.

        </p>

        <div className="reveal soft-panel mt-12 flex flex-col gap-6 p-7 sm:flex-row sm:items-center sm:justify-between [--d:180ms]">
          <div className="max-w-xl">
            <p className="micro-label">Beta seat — $0</p>
            <h2 className="etched mt-4 text-2xl font-semibold">
              Free while we're in beta, in exchange for real feedback.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              You get Operator-level access to everything below: unlimited businesses, daily moves,
              monthly bold moves, journal, phonebook and full history. In exchange, send one substantive note a
              week — a bug, a rough edge, or an idea worth building. Your seat stays free until we go
              live; when billing opens, beta testers get a founders discount as our thanks. Beta ends
              with 14 days' notice — you're never charged automatically, and
              if you skip a plan you keep export-only access for 30 days. If the feedback stops, we
              may close the seat. Your data is always yours to export.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground/70">
              The founders discount amount and duration are set at launch, are discretionary and
              personal to you, and have no cash value.
            </p>

          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-3">
            <Link
              to="/auth"
              search={{ beta: "1" }}
              className="sheen inline-flex shrink-0 items-center justify-center rounded-sm bg-primary px-6 py-3.5 text-sm font-semibold tracking-wide text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
            >
              Take a free beta seat
            </Link>
            <DemoDoor className="justify-center" label="Or peek inside — no signup" />
          </div>
        </div>

        <div className="reveal mt-16 text-center [--d:185ms]">
          <p className="micro-label">After launch</p>
          <h2 className="etched mt-3 text-2xl font-semibold">
            What it will cost once billing opens
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            All four options below are free for you during beta. Prices are shown up front so there
            are no surprises later — nothing is charged automatically.
          </p>
        </div>

        <div className="reveal mt-8 flex items-center justify-center [--d:190ms]">
          <div className="inline-flex items-center rounded-sm border border-border p-1">
            {(["annual", "monthly"] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCadence(c)}
                aria-pressed={cadence === c}
                className={`rounded-sm px-4 py-2 text-xs font-semibold tracking-wide transition-colors ${
                  cadence === c
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {c === "annual" ? "Yearly — save ~25%" : "Monthly"}
              </button>
            ))}
          </div>
        </div>


        <div className="mt-8 grid items-center gap-5 lg:grid-cols-3">
          {TIERS.map((tier, i) => (
            <div
              key={tier.code}
              className={`reveal flex flex-col ${
                tier.featured
                  ? "vault-panel z-10 p-9 shadow-2xl ring-2 ring-primary/50 lg:-my-6 lg:scale-[1.04]"
                  : "soft-panel p-7 lg:opacity-95"
              }`}
              style={{ ["--d" as string]: `${200 + i * 110}ms` }}
            >
              <div className="flex items-baseline justify-between">
                <p className="micro-label">{tier.name}</p>
                {tier.featured ? (
                  <span className="rounded-sm bg-primary px-2 py-1 text-[0.625rem] font-semibold tracking-[0.2em] text-primary-foreground">
                    MOST CHOSEN
                  </span>
                ) : (
                  <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 tabular-nums">
                    {tier.code}
                  </span>
                )}
              </div>
              <p className="mt-6 inline-flex items-center gap-2 self-start rounded-sm border border-primary/40 bg-primary/10 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.16em] text-primary">
                FREE DURING BETA
              </p>
              <p
                className={`etched mt-4 font-semibold ${tier.featured ? "text-5xl" : "text-4xl"} text-muted-foreground`}
              >
                {cadence === "annual" ? money(tier.annual) : money(tier.monthly)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {cadence === "annual" ? "per year after launch" : "per month after launch"}
              </p>
              <p className="mt-2 text-xs text-muted-foreground/80">
                {cadence === "annual"
                  ? `About ${perDay(tier.annual)} — ${money(tier.monthly * 12 - tier.annual)} less than monthly`
                  : `About ${perDay(tier.monthly * 12)}`}
              </p>

              <p className="mt-5 text-sm text-muted-foreground">{tier.line}</p>

              <ul className="mt-7 flex-1 space-y-3">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/auth"
                search={{ beta: "1" }}
                className={`sheen mt-8 inline-flex items-center justify-center rounded-sm px-6 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-0.5 ${
                  tier.featured
                    ? "bg-primary py-4 text-base text-primary-foreground"
                    : "border border-border py-3.5 text-foreground hover:border-foreground/40"
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>


        <section id="offer" className="reveal mt-14 grid gap-6 lg:grid-cols-[1fr_1fr] [--d:120ms]">
          <DiscountOffer />
          <div className="soft-panel flex flex-col justify-between p-7">
            <div>
              <p className="micro-label">Founders Edition — one time</p>
              <p className="mt-4 inline-flex items-center gap-2 self-start rounded-sm border border-primary/40 bg-primary/10 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.16em] text-primary">
                FREE DURING BETA
              </p>
              <p className="etched mt-4 text-4xl font-semibold text-muted-foreground">$1,497</p>
              <p className="mt-1 text-xs text-muted-foreground">
                paid once after launch, kept for life
              </p>

              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Top-tier Vault access forever, every instrument we ship from here on, and your name
                inscribed on the founders wall. This is not the budget option — it's the commitment
                option. If you plan to run businesses here for years, it pays for itself in roughly
                four. When the round opens, it'll stay open until we hit our goal — then it's closed permanently.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground/70">
                Most operators choose Vault yearly. The Founders Edition is for the small group who
                want to own the tool outright.
              </p>
            </div>
            <Link
              to="/founders"
              className="mt-8 inline-flex items-center justify-center rounded-sm border border-border px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors hover:border-foreground/40"
            >
              See the founders wall
            </Link>
          </div>
        </section>


        <section className="reveal mt-16 border-t border-border/60 pt-10 [--d:500ms]">
          <p className="micro-label">After beta — the free seat</p>
          <h2 className="etched mt-4 max-w-2xl text-2xl font-semibold sm:text-3xl">
            You never lose access. You lose the long memory.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            When billing opens, the daily move, the evening close and the monthly bold move stay free
            for everyone — forever. What paying buys is the part that compounds: the record that
            never rolls off, and the ability to take it with you.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <div className="soft-panel p-7">
              <p className="micro-label">Free, honestly limited</p>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                {[
                  "One business",
                  "Daily move + evening close, unlimited",
                  "Monthly bold move",
                  "History kept for the last 30 days",
                  "Journal and contacts, capped at 25 entries",
                  "No export",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="soft-panel p-7">
              <p className="micro-label">What you'd be giving up</p>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                {[
                  "A streak older than a month — gone quietly as it rolls off",
                  "Every business but one, side by side",
                  "The full timeline, filterable by type and category",
                  "One-tap export of everything you've written",
                  "Encrypted sync across your devices",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Nothing is deleted the day a plan ends — your history is always yours to export before
            anything rolls off, and we tell you before it does.
          </p>
        </section>

        <div className="reveal mt-12 grid gap-6 border-t border-border/60 pt-10 sm:grid-cols-2 [--d:520ms]">
          <div>
            <p className="micro-label">Why we charge.</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Free products sell your data, your focus, or both. The Founders App is funded by the people
              who use it. That keeps the incentives clean.
            </p>
          </div>
          <div>
            <p className="micro-label">7-day money-back guarantee</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              If The Founders App doesn't fit the way you work, email us within seven days for a full
              refund. No forms, no friction.

            </p>
          </div>
        </div>

        <section className="reveal mt-14 border-t border-border/60 pt-10 [--d:120ms]">
          <p className="micro-label">On the workbench</p>
          <h2 className="etched mt-4 max-w-2xl text-2xl font-semibold sm:text-3xl">
            Works in any browser today.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            The Founders App runs in any browser today and installs to your home screen full-screen.
            Additional platforms may come later. Every plan includes them the day they ship, at no
            extra cost.
          </p>
        </section>

        <section id="invite" className="reveal mt-20 border-t border-border/60 pt-12 [--d:120ms]">
          <p className="micro-label">Invite the people you trust</p>
          <h2 className="etched mt-4 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Three people join, your next month is on us.
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Every member gets a private invite link. Anyone approved through it gets 90 days of The
            Founders App once billing is live. For every three people who join on your link, we credit
            a free month to your account — no cap, no expiry, no countdown clock.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              { n: "01", t: "Share your link", d: "Found in the app under Invite." },
              { n: "02", t: "They get 90 days", d: "Full plan access, no card required upfront." },
              { n: "03", t: "You get a month", d: "Credited automatically at every third join." },
            ].map((s) => (
              <div key={s.n} className="soft-panel p-6">
                <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 tabular-nums">
                  {s.n}
                </span>
                <p className="mt-4 text-sm font-semibold">{s.t}</p>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="reveal mt-16 soft-panel p-7">
          <p className="micro-label">Why the loop works</p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            The Founders App's daily loop is assembled from published research on goal specificity,
            implementation intentions (naming when and where), and recorded progress monitoring.{" "}
            <Link to="/method" className="underline underline-offset-2 hover:text-foreground">
              Read the method
            </Link>
            .
          </p>
          <p className="mt-4 max-w-2xl text-xs leading-relaxed text-muted-foreground/80">
            Fine print: we have not run our own scientific experiment and make no medical,
            psychological, or financial claims. The research cited was conducted by others in
            different settings. What we can say honestly is that this formula — one specific move, a
            stated time and place, an honest evening review, and a record you keep — has produced
            consistent results for the founders we've watched use it, across very different
            businesses. Outcomes depend on your work, not on the software.
          </p>
        </section>



        <section id="bulk" className="reveal mt-20 border-t border-border/60 pt-12 [--d:120ms]">
          <p className="micro-label">Teams and companies</p>
          <h2 className="etched mt-4 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Bulk licenses, priced down as you scale.
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Buying for a leadership team, a portfolio of operators, or a franchise group? Licenses
            are billed on one invoice, assigned and reassigned by you, and every member's data stays
            private to them.
          </p>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Discounts come off the annual price — Operator at $179 a year becomes $143 per person at
            10 licenses. Free during the beta, so nothing is billed until we launch paid plans.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {BULK.map((b) => (
              <div key={b.range} className="soft-panel p-6">
                <p className="micro-label">{b.range}</p>
                <p className="etched mt-4 text-3xl font-semibold">{b.off}</p>
                <p className="mt-2 text-sm text-muted-foreground">{b.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <BulkInquiryForm />
            <TeamInterestForm />
          </div>

          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              "Annual invoicing, PO and bank transfer accepted",
              "Assign and reassign licenses as your team changes",
              "Each member's entries stay private — no manager view",
              "Concierge onboarding at 25 licenses and above",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{f}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
